import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { productionApi as P, PRIORITY_STYLE, STAGE_TITLE } from "@/lib/production";
import { StatusChip } from "@/components/production/StatusChip";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Loader2, MoreHorizontal, AlertTriangle, CheckCircle2, Truck, GitBranch, Factory } from "lucide-react";

function Kpi({ label, value, tone = "slate", testid }) {
  const tones = { slate: "text-slate-900", rose: "text-rose-600", emerald: "text-emerald-600", indigo: "text-indigo-600", amber: "text-amber-600" };
  return (
    <Card className="p-4" data-testid={testid}>
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-bold ${tones[tone]}`}>{value}</p>
    </Card>
  );
}

export default function SupervisorDashboard() {
  const { stage } = useParams();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lineage, setLineage] = useState(null);
  const canApprove = can("production", "approve");

  const load = useCallback(async () => {
    try { setData(await P.board(stage)); } finally { setLoading(false); }
  }, [stage]);

  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => { const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const showLineage = async (batchId) => {
    try { setLineage(await P.lineage(batchId)); } catch (e) { toast.error("No lineage"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  const { kpi, tasks, machines, alerts, ready_for_dispatch = [], a_grade_pct } = data;

  return (
    <div className="space-y-6" data-testid={`supervisor-${stage}`}>
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{STAGE_TITLE[stage]} — Supervisor</h2>
        <p className="text-sm text-slate-500">Live floor control & oversight</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi label="Meters today" value={`${kpi.meters_today} m`} tone="indigo" testid="kpi-meters" />
        <Kpi label="Utilisation" value={`${kpi.utilisation_pct}%`} testid="kpi-util" />
        <Kpi label="Wastage %" value={`${kpi.wastage_pct}%`} tone={kpi.wastage_pct > 5 ? "rose" : "slate"} testid="kpi-wastage" />
        <Kpi label="At risk" value={kpi.at_risk} tone={kpi.at_risk ? "rose" : "slate"} testid="kpi-atrisk" />
        <Kpi label={stage === "packing" ? "A-grade %" : "Awaiting approval"} value={stage === "packing" ? `${a_grade_pct}%` : kpi.awaiting_approval} tone="amber" testid="kpi-extra" />
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="p-4" data-testid="alerts-panel">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><AlertTriangle className="h-4 w-4 text-amber-500" />Alerts</p>
          <div className="space-y-1.5">
            {alerts.map((a, i) => (
              <div key={i} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm">
                <span className="text-slate-700">{a.message}</span>
                {a.type === "approval" && canApprove && (
                  <Button size="sm" variant="outline" className="h-7 border-emerald-300 text-emerald-700"
                    onClick={() => act(() => P.approveWastage(a.task_id), "Approved")} data-testid={`approve-wastage-${a.task_id}`}>Approve</Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Queue table */}
        <Card className="p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Queue & active jobs</p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Job</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Customer</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Progress</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Delivery</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Priority</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((t) => (
                  <TableRow key={t.id} data-testid={`board-row-${t.job_no}`}>
                    <TableCell className="font-medium text-slate-800">{t.job_no}</TableCell>
                    <TableCell className="text-slate-600">{t.customer}</TableCell>
                    <TableCell className="tabular-nums text-slate-600">{t.done_meters}/{t.target_meters} m</TableCell>
                    <TableCell className={t.delivery_date && new Date(t.delivery_date) <= new Date(Date.now() + 2 * 864e5) ? "text-rose-600" : "text-slate-500"}>{t.delivery_date || "—"}</TableCell>
                    <TableCell><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${PRIORITY_STYLE[t.priority]}`}>{t.priority}</span></TableCell>
                    <TableCell><StatusChip status={t.status} /></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`board-actions-${t.job_no}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {t.output_batches?.[0] && <DropdownMenuItem onClick={() => showLineage(t.output_batches[t.output_batches.length - 1].id)}><GitBranch className="mr-2 h-4 w-4" />View lineage</DropdownMenuItem>}
                          {canApprove && t.wastage_pending && <DropdownMenuItem onClick={() => act(() => P.approveWastage(t.id), "Approved")}>Approve over-wastage</DropdownMenuItem>}
                          {canApprove && !t.on_hold && <DropdownMenuItem onClick={() => act(() => P.hold(t.id, "Supervisor hold"), "On hold")}>Hold</DropdownMenuItem>}
                          {canApprove && t.on_hold && <DropdownMenuItem onClick={() => act(() => P.release(t.id), "Released")}>Release hold</DropdownMenuItem>}
                          {canApprove && <DropdownMenuItem className="text-rose-600" onClick={() => act(() => P.cancel(t.id, "SO change"), "Cancelled")}>Cancel task</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {tasks.length === 0 && <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-400" data-testid="board-empty">No jobs in the {stage} queue.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Machines + ready-for-dispatch */}
        <div className="space-y-4">
          <Card className="p-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Machines</p>
            {machines.length === 0 && <p className="text-sm text-slate-400" data-testid="no-machines">No {stage} machines configured.</p>}
            <div className="space-y-2">
              {machines.map((m) => (
                <div key={m.machine_id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid={`machine-${m.machine_id}`}>
                  <span className="flex items-center gap-2 text-sm text-slate-700"><Factory className="h-4 w-4 text-slate-400" />{m.name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.status_pill === "running" ? "bg-indigo-50 text-indigo-700" : m.status_pill === "paused" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{m.status_pill}</span>
                </div>
              ))}
            </div>
          </Card>

          {stage === "packing" && (
            <Card className="p-4" data-testid="ready-dispatch-panel">
              <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Truck className="h-4 w-4" />Ready for dispatch ({ready_for_dispatch.length})</p>
              {ready_for_dispatch.length === 0 && <p className="text-sm text-slate-400">Nothing ready yet.</p>}
              <div className="space-y-2">
                {ready_for_dispatch.map((r) => (
                  <div key={r.id} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-sm" data-testid={`ready-${r.batch_id}`}>
                    <button className="text-left" onClick={() => showLineage(r.batch_id)}>
                      <span className="font-medium text-slate-800">{r.batch_id}</span>
                      <span className="block text-xs text-slate-500">{r.meters} m · Grade {r.grade} · {r.customer}</span>
                    </button>
                    {canApprove && (
                      <Button size="sm" className="h-7 bg-emerald-600 text-white hover:bg-emerald-700"
                        onClick={() => act(() => P.releaseDispatch(tasks.find((t) => t.job_no === r.job_no)?.id), "Released to Dispatch")}
                        data-testid={`release-dispatch-${r.batch_id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Release</Button>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Lineage drawer */}
      <Sheet open={!!lineage} onOpenChange={(o) => !o && setLineage(null)}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Batch lineage</SheetTitle>
            <SheetDescription>Full traceability chain for {lineage?.batch_id}</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-3">
            {lineage?.chain?.map((c, i) => (
              <div key={c.id} className="relative rounded-md border border-slate-200 p-3" data-testid={`lineage-node-${c.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800">{c.id}</span>
                  <span className="text-xs uppercase text-slate-400">{c.stage}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">out {c.meters_out} m{c.wastage ? ` · waste ${c.wastage} m` : ""}{c.shrinkage ? ` · shrink ${c.shrinkage} m` : ""}{c.grade ? ` · grade ${c.grade}` : ""}</p>
                {i < lineage.chain.length - 1 && <div className="ml-1 mt-2 text-slate-300">↑ from</div>}
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
